'use client';
import '@/components/RelatedArticles/swiper-overrides.css';
import 'swiper/css';
import 'swiper/css/navigation';

import { Box, Stack } from '@mui/material';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { PageCard } from '@/components/Card/PageCard';
import { getImageUrl } from '@/lib/helpers/getImageUrl';
import { ResourceResultType } from '@/lib/queries/Resources';
import { ResourceCategory } from '@/types';

// Same card component + row/carousel layout as RelatedArticles, so Latest Resources and
// Related Articles read as one visual family instead of two differently-styled blocks.
export function LatestResourcesCards({ items }: { items: ResourceResultType[] }) {
  const renderCard = (resource: ResourceResultType) => {
    const category = resource.ResourceCategory as ResourceCategory | undefined;
    const slug = category ? `resources/${category.Slug}/${resource.Slug}` : resource.Slug;
    const imageUrl =
      getImageUrl({ image: resource.ResourceThumbnail, size: 'url' }) ||
      '/assets/placeholder-thumbnail.png';

    return (
      <PageCard
        key={resource.Slug}
        slug={slug}
        title={resource.ResourceTitle}
        paragraph={resource.ResourceDescription ?? undefined}
        imageUrl={imageUrl}
      />
    );
  };

  if (items.length > 3) {
    return (
      <Swiper
        modules={[Navigation]}
        navigation
        spaceBetween={24}
        slidesPerView={1.1}
        style={{ paddingBottom: '2px' }}
        breakpoints={{
          600: { slidesPerView: 2.1 },
          900: { slidesPerView: 3 },
        }}
      >
        {items.map((resource) => (
          <SwiperSlide key={resource.Slug} style={{ height: 'auto' }}>
            {renderCard(resource)}
          </SwiperSlide>
        ))}
      </Swiper>
    );
  }

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 2, sm: 3 }}
      justifyContent="space-between"
      sx={{ p: 1, pb: 2 }}
    >
      {items.map((resource) => (
        <Box key={resource.Slug} sx={{ flex: 1, minWidth: 0 }}>
          {renderCard(resource)}
        </Box>
      ))}
    </Stack>
  );
}
